<template>
    <!-- Patient Sub Panel -->
    <v-expansion-panel focusable>
        <template v-slot:title>
            {{ getHeader }}
        </template>
        <v-expansion-panel-text>
            <v-card class="bg-aqua-teal">
                <v-card-title class="text-center text-wrap">
                    {{ getTitle }}
                </v-card-title>
                <v-card-text>
                    {{ getText }}
                </v-card-text>
            </v-card>

            <!-- Diagnosis dropdown selector -->
            <v-autocomplete v-if="getEditable && updateKey === 'diagnosis'" v-model="selectedDiagnosisCode"
                :items="diagnosisCodes" :loading="isLoading" label="Search for diagnosis code"
                placeholder="Search by code or description..." return-object clearable
                @update:model-value="updateTextValue" @update:search="searchDiagnosisCodes"
                :menu-props="{ maxHeight: '300px' }" density="compact" hide-no-data hide-details item-value="code"
                item-title="codeDescription">
                <template v-slot:selection>
                    <div>
                        {{ selectedDiagnosisCode ? selectedDiagnosisCode.code : 'No code' }} -
                        {{ selectedDiagnosisCode ? selectedDiagnosisCode.codeDescription : 'No description' }}
                    </div>
                </template>
                <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :title="`${item.raw.code} - ${item.raw.codeDescription}`">
                        <template v-slot:subtitle></template>
                    </v-list-item>
                </template>
            </v-autocomplete>

            <!-- Regular text area for non-diagnosis fields -->
            <v-textarea v-if="getEditable && updateKey !== 'diagnosis'" :label="`Editing ${getTitle}`"
                v-model="textValue" clearable />
            <div class="d-flex justify-end mt-2">
                <v-btn v-if="!getEditable" class="bg-aqua-teal" @click="toggle">Edit</v-btn>
                <v-btn v-if="getEditable" class="bg-aqua-teal mr-2" @click="update">Update</v-btn>
                <v-btn v-if="getEditable" class="bg-aqua-teal" @click="toggle">Back</v-btn>
            </div>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>

<script>
import axios from 'axios';

export default {
    name: "PatientSubPanel",
    data() {
        return {
            readonly: false,
            textValue: "",
            diagnosisCodes: [],
            selectedDiagnosisCode: null,
            isLoading: false,
            searchTimeout: null
        }
    },
    props: ['header', 'title', 'text', 'updateKey'],
    emits: ['updatePatientData'],
    methods: {
        toggle() {
            this.readonly = !this.readonly;

            // When opening the edit mode for diagnosis, initialize with empty array
            if (this.readonly && this.updateKey === 'diagnosis') {
                this.diagnosisCodes = [];
                this.selectedDiagnosisCode = null;

                // If there's an existing diagnosis, try to fetch it
                if (this.text) {
                    const codeMatch = this.text.match(/^([A-Z0-9]+) -/);
                    if (codeMatch && codeMatch[1]) {
                        this.fetchSpecificDiagnosisCode(codeMatch[1]);
                    }
                }
            }
        },

        updateTextValue(value) {
            console.log("Selected value:", value);
            console.log("Value type:", typeof value);
            console.log("Value properties:", value ? Object.keys(value) : "No value");

            // Check if value exists and has the expected structure
            if (value && typeof value === 'object') {
                const code = value.code || (value.raw && value.raw.code);
                const description = value.codeDescription || (value.raw && value.raw.codeDescription);

                console.log("Extracted code:", code);
                console.log("Extracted description:", description);

                if (code && description) {
                    this.textValue = `${code} - ${description}`;
                    console.log("Set textValue to:", this.textValue);
                } else {
                    // Handle case where object exists but doesn't have expected properties
                    console.log("Missing code or description in selection");
                    this.textValue = "";
                }
            } else if (!value) {
                console.log("No value provided, clearing fields");
                this.textValue = "";
                this.selectedDiagnosisCode = null;
            }
        },

        // Fetch a single diagnosis code by its ID
        async fetchSpecificDiagnosisCode(code) {
            this.isLoading = true;
            try {
                axios.defaults.baseURL = process.env.VUE_APP_API_URL;
                const response = await axios.get(`/api/diagnosiscode/${code}`);

                if (response.data) {
                    // Add this code to our local array and select it
                    this.diagnosisCodes = [response.data];
                    this.selectedDiagnosisCode = response.data;
                }
            } catch (error) {
                console.error(`Error fetching specific diagnosis code ${code}:`, error);
            } finally {
                this.isLoading = false;
            }
        },

        // This will be called whenever the user types in the search field
        async searchDiagnosisCodes(search) {
            if (!search || search.length < 2) {
                this.diagnosisCodes = [];
                return;
            }

            this.isLoading = true;

            try {
                axios.defaults.baseURL = process.env.VUE_APP_API_URL;

                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }

                await new Promise(resolve => {
                    this.searchTimeout = setTimeout(resolve, 300);
                });

                // Check if the search looks like a diagnosis code
                const isCodeSearch = /^[A-Z0-9]+$/i.test(search);

                if (isCodeSearch) {
                    // Try direct code lookup first
                    try {
                        const response = await axios.get(`/api/diagnosiscode/${search}`);
                        if (response.data) {
                            // Handle both single object and array responses
                            const result = Array.isArray(response.data) ? response.data : [response.data];
                            this.diagnosisCodes = result;
                            return;
                        }
                    } catch (codeError) {
                        console.log("No exact code match found, continuing with search...");
                    }
                }

                // Use the search endpoint with term parameter
                const response = await axios.get(`/api/diagnosiscode/search?term=${search}`);

                // Make sure we're handling the array response correctly
                const results = Array.isArray(response.data) ? response.data.slice(0, 30) : [];

                // Remove duplicates by keeping only unique code values
                const uniqueCodes = new Set();
                const uniqueResults = results.filter(item => {
                    if (item && item.code && !uniqueCodes.has(item.code)) {
                        uniqueCodes.add(item.code);
                        return true;
                    }
                    return false;
                });

                // Set results directly for immediate update
                this.diagnosisCodes = uniqueResults;
            } catch (error) {
                console.error("Error searching diagnosis codes:", error);
                this.diagnosisCodes = [];
            } finally {
                this.isLoading = false;
            }
        },

        update() {
            /* Emit the updated key-value pair to the 
            Patient parent object for handling.*/
            if (this.updateKey) {
                const key = this.updateKey;
                const value = this.textValue;
                const updatedData = {
                    "key": key,
                    "value": value
                };
                this.$emit('updatePatientData', updatedData);
                this.toggle();
            }
        },
    },
    computed: {
        getEditable() {
            return this.readonly;
        },
        getHeader() {
            if (this.header) {
                return this.header;
            } else {
                return "Unavailable";
            }
        },
        getText() {
            if (this.text) {
                return this.text;
            } else {
                return "Unavailable";
            }
        },
        getTitle() {
            if (this.title) {
                return this.title;
            } else {
                return "Unavailable";
            }
        }
    },
    mounted() {
        this.textValue = `${this.getText}`;
    }
}
</script>

<style>
.bg-aqua-teal {
    background-color: #08b6aa;
}
</style>